import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Kanban,
  Search,
  Mail,
  Calendar,
  Clock,
  AlertCircle,
  ChevronRight,
  Sparkles,
  Users,
  FileText,
  Trash2,
  RefreshCw,
  Send,
  ExternalLink,
  MessageSquare,
  Plus,
  Target,
  Lightbulb,
  XCircle,
  CheckCircle,
  Building2,
  User,
  ArrowRight,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_CONFIG = {
  researching: {
    label: "Researching",
    color: "bg-slate-100 text-slate-700 border-slate-300",
    icon: Search,
    description: "Preparing intro strategy"
  },
  contacted: {
    label: "Contacted",
    color: "bg-blue-100 text-blue-700 border-blue-300",
    icon: Mail,
    description: "Awaiting response"
  },
  meeting_scheduled: {
    label: "Meeting Scheduled",
    color: "bg-purple-100 text-purple-700 border-purple-300",
    icon: Calendar,
    description: "Prepare for meeting"
  },
  term_sheet: {
    label: "Term Sheet",
    color: "bg-green-100 text-green-700 border-green-300",
    icon: FileText,
    description: "Negotiating terms"
  }
};

export default function Pipeline() {
  return (
    <AppLayout>
      <PipelineContent />
    </AppLayout>
  );
}

function PipelineContent() {
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState({
    researching: [],
    contacted: [],
    meeting_scheduled: [],
    term_sheet: []
  });
  const [stageCounts, setStageCounts] = useState({});
  const [followupsDue, setFollowupsDue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [emailData, setEmailData] = useState({ to_email: "", subject: "", body: "", auto_followup_days: 5 });
  const [stageData, setStageData] = useState({ stage: "", followup_reminder_days: 5, meeting_date: "", meeting_link: "" });
  const [sending, setSending] = useState(false);

  const fetchPipeline = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/pipeline`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPipeline(data.pipeline);
        setStageCounts(data.stage_counts);
        setFollowupsDue(data.followups_due || []);
      }
    } catch (err) {
      console.error("Error fetching pipeline:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  const handleStageChange = async (pipelineId, newStage) => {
    try {
      const res = await fetch(`${API_URL}/api/pipeline/${pipelineId}/stage`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          stage: newStage,
          followup_reminder_days: stageData.followup_reminder_days,
          meeting_date: stageData.meeting_date || null,
          meeting_link: stageData.meeting_link || null
        })
      });
      if (res.ok) {
        toast.success(`Moved to ${STAGE_CONFIG[newStage].label}`);
        fetchPipeline();
        setShowStageModal(false);
      }
    } catch (err) {
      toast.error("Failed to update stage");
    }
  };

  const handleSendEmail = async () => {
    if (!emailData.to_email || !emailData.subject || !emailData.body) {
      toast.error("Please fill in all email fields");
      return;
    }
    
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/pipeline/${selectedItem.pipeline_id}/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(emailData)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Email sent! Follow-up reminder set for ${emailData.auto_followup_days} days`);
        setShowEmailModal(false);
        setEmailData({ to_email: "", subject: "", body: "", auto_followup_days: 5 });
        fetchPipeline();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to send email");
      }
    } catch (err) {
      toast.error("Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const handleRemove = async (pipelineId) => {
    if (!window.confirm("Remove this investor from your pipeline?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/pipeline/${pipelineId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Removed from pipeline");
        fetchPipeline();
        setShowDetailModal(false);
      }
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  const handleRefreshIntroPath = async (pipelineId) => {
    try {
      const res = await fetch(`${API_URL}/api/pipeline/${pipelineId}/refresh-intro-path`, {
        method: "POST",
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Intro strategy refreshed");
        fetchPipeline();
        // Refresh selected item if in modal
        if (selectedItem?.pipeline_id === pipelineId) {
          const itemRes = await fetch(`${API_URL}/api/pipeline/${pipelineId}`, { credentials: "include" });
          if (itemRes.ok) {
            setSelectedItem(await itemRes.json());
          }
        }
      }
    } catch (err) {
      toast.error("Failed to refresh");
    }
  };

  const openDetailModal = async (item) => {
    try {
      const res = await fetch(`${API_URL}/api/pipeline/${item.pipeline_id}`, { credentials: "include" });
      if (res.ok) {
        setSelectedItem(await res.json());
        setShowDetailModal(true);
      }
    } catch (err) {
      setSelectedItem(item);
      setShowDetailModal(true);
    }
  };

  const openEmailModal = (item) => {
    setSelectedItem(item);
    setEmailData({
      to_email: item.primary_contact_email || "",
      subject: `Introduction - ${item.firm_name}`,
      body: "",
      auto_followup_days: 5
    });
    setShowEmailModal(true);
  };

  const openStageModal = (item, targetStage) => {
    setSelectedItem(item);
    setStageData({
      stage: targetStage,
      followup_reminder_days: 5,
      meeting_date: "",
      meeting_link: ""
    });
    setShowStageModal(true);
  };

  const PipelineCard = ({ item, stage }) => {
    const config = STAGE_CONFIG[stage];
    const introPath = item.intro_path;
    const hasFollowupDue = followupsDue.some(f => f.pipeline_id === item.pipeline_id);
    
    return (
      <Card 
        className={`mb-3 cursor-pointer hover:shadow-md transition-shadow ${hasFollowupDue ? 'ring-2 ring-amber-400' : ''}`}
        onClick={() => openDetailModal(item)}
        data-testid={`pipeline-card-${item.pipeline_id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900 truncate">{item.firm_name}</h4>
              {item.primary_contact_name && (
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {item.primary_contact_name}
                </p>
              )}
            </div>
            {item.match_score && (
              <Badge variant="outline" className="text-xs">
                {item.match_score.toFixed(1)}/10
              </Badge>
            )}
          </div>
          
          {/* Stage-specific content */}
          {stage === "researching" && introPath && (
            <div className="mt-2 p-2 bg-slate-50 rounded text-xs">
              {introPath.path_type === "event" && (
                <div className="flex items-center gap-1 text-purple-600">
                  <Calendar className="w-3 h-3" />
                  <span className="font-medium">{introPath.partner_attending}</span> at {introPath.event_name}
                </div>
              )}
              {introPath.path_type === "warm_intro" && (
                <div className="flex items-center gap-1 text-green-600">
                  <Users className="w-3 h-3" />
                  Intro via <span className="font-medium">{introPath.mutual_connection}</span>
                </div>
              )}
              {introPath.path_type === "cold_email" && (
                <div className="flex items-center gap-1 text-slate-500">
                  <Mail className="w-3 h-3" />
                  Cold outreach
                </div>
              )}
            </div>
          )}
          
          {stage === "contacted" && (
            <div className="mt-2">
              {hasFollowupDue ? (
                <div className="flex items-center gap-1 text-amber-600 text-xs font-medium">
                  <AlertCircle className="w-3 h-3" />
                  Follow-up due!
                </div>
              ) : item.followup_reminder_date && (
                <div className="flex items-center gap-1 text-slate-500 text-xs">
                  <Clock className="w-3 h-3" />
                  Follow-up: {new Date(item.followup_reminder_date).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
          
          {stage === "meeting_scheduled" && item.meeting_date && (
            <div className="mt-2 flex items-center gap-1 text-purple-600 text-xs">
              <Calendar className="w-3 h-3" />
              {new Date(item.meeting_date).toLocaleDateString()}
            </div>
          )}
          
          {/* Quick actions */}
          <div className="mt-3 flex gap-2" onClick={e => e.stopPropagation()}>
            {stage === "researching" && (
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1 text-xs h-7"
                onClick={() => openEmailModal(item)}
              >
                <Mail className="w-3 h-3 mr-1" />
                Contact
              </Button>
            )}
            {stage === "contacted" && (
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1 text-xs h-7"
                onClick={() => openStageModal(item, "meeting_scheduled")}
              >
                <Calendar className="w-3 h-3 mr-1" />
                Schedule Meeting
              </Button>
            )}
            {stage === "meeting_scheduled" && (
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1 text-xs h-7"
                onClick={() => navigate("/strategy")}
              >
                <Sparkles className="w-3 h-3 mr-1" />
                Prepare
              </Button>
            )}
            {stage === "term_sheet" && (
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1 text-xs h-7"
                onClick={() => window.open("https://www.ycombinator.com/documents", "_blank")}
              >
                <FileText className="w-3 h-3 mr-1" />
                Templates
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Kanban className="w-7 h-7 text-teal-600" />
            CRM
          </h1>
          <p className="text-slate-600">Track your investor outreach progress</p>
        </div>
        <Button onClick={() => navigate("/investors")} className="gap-2 bg-teal-600 hover:bg-teal-700">
          <Plus className="w-4 h-4" />
          Add Investors
        </Button>
      </div>
      
      {/* Follow-ups Due Alert */}
      {followupsDue.length > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">
                {followupsDue.length} follow-up{followupsDue.length > 1 ? 's' : ''} due
              </p>
              <p className="text-sm text-amber-700">
                {followupsDue.map(f => f.firm_name).join(", ")}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(STAGE_CONFIG).map(([stage, config]) => {
          const StageIcon = config.icon;
          const items = pipeline[stage] || [];
          
          return (
            <div key={stage} className="flex flex-col">
              <div className={`p-3 rounded-t-lg border-2 ${config.color}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StageIcon className="w-4 h-4" />
                    <span className="font-semibold">{config.label}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {items.length}
                  </Badge>
                </div>
                <p className="text-xs mt-1 opacity-75">{config.description}</p>
              </div>
              
              <div className="flex-1 bg-slate-50 border-x-2 border-b-2 border-slate-200 rounded-b-lg p-3 min-h-[200px]">
                {items.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-slate-400">
                    <Kanban className="w-8 h-8 mb-2 opacity-50" />
                    <p className="text-sm">No investors</p>
                  </div>
                ) : (
                  items.map(item => (
                    <PipelineCard key={item.pipeline_id} item={item} stage={stage} />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedItem && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-teal-600" />
                  {selectedItem.firm_name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Current Stage */}
                <div className="flex items-center gap-2">
                  <Badge className={STAGE_CONFIG[selectedItem.stage]?.color}>
                    {STAGE_CONFIG[selectedItem.stage]?.label}
                  </Badge>
                  {selectedItem.match_score && (
                    <Badge variant="outline">{selectedItem.match_score.toFixed(1)}/10 fit</Badge>
                  )}
                </div>
                
                {/* Contact Info */}
                {selectedItem.primary_contact_name && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm font-medium text-slate-700">Primary Contact</p>
                    <p className="text-slate-900">{selectedItem.primary_contact_name}</p>
                    {selectedItem.primary_contact_role && (
                      <p className="text-sm text-slate-500">{selectedItem.primary_contact_role}</p>
                    )}
                    {selectedItem.primary_contact_email && (
                      <p className="text-sm text-teal-600">{selectedItem.primary_contact_email}</p>
                    )}
                  </div>
                )}
                
                {/* Intro Path Strategy (for Researching stage) */}
                {selectedItem.stage === "researching" && selectedItem.intro_path && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                        <Target className="w-4 h-4 text-teal-600" />
                        Intro Strategy
                      </h4>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleRefreshIntroPath(selectedItem.pipeline_id)}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Refresh
                      </Button>
                    </div>
                    
                    <div className={`p-3 rounded-lg ${
                      selectedItem.intro_path.path_type === "event" ? "bg-purple-50 border border-purple-200" :
                      selectedItem.intro_path.path_type === "warm_intro" ? "bg-green-50 border border-green-200" :
                      "bg-slate-50 border border-slate-200"
                    }`}>
                      <p className="font-medium text-sm">{selectedItem.intro_path.description}</p>
                      {selectedItem.intro_path.confidence && (
                        <Badge variant="outline" className="mt-2 text-xs">
                          {selectedItem.intro_path.confidence} confidence
                        </Badge>
                      )}
                    </div>
                    
                    {selectedItem.intro_path.standout_strategy && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm font-medium text-amber-800 flex items-center gap-1">
                          <Lightbulb className="w-4 h-4" />
                          How to Stand Out
                        </p>
                        <p className="text-sm text-amber-900 mt-1">
                          {selectedItem.intro_path.standout_strategy}
                        </p>
                      </div>
                    )}
                    
                    {selectedItem.intro_path.talking_points?.length > 0 && (
                      <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                        <p className="text-sm font-medium text-teal-800 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Talking Points
                        </p>
                        <ul className="mt-2 space-y-1">
                          {selectedItem.intro_path.talking_points.map((point, i) => (
                            <li key={i} className="text-sm text-teal-900 flex items-start gap-2">
                              <ChevronRight className="w-3 h-3 mt-1 flex-shrink-0" />
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {selectedItem.intro_path.avoid?.length > 0 && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm font-medium text-red-800 flex items-center gap-1">
                          <XCircle className="w-4 h-4" />
                          Avoid
                        </p>
                        <ul className="mt-2 space-y-1">
                          {selectedItem.intro_path.avoid.map((item, i) => (
                            <li key={i} className="text-sm text-red-900 flex items-start gap-2">
                              <ChevronRight className="w-3 h-3 mt-1 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Follow-up Reminder (for Contacted stage) */}
                {selectedItem.stage === "contacted" && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Follow-up Reminder
                    </p>
                    {selectedItem.followup_reminder_date ? (
                      <p className="text-sm text-blue-900 mt-1">
                        {new Date(selectedItem.followup_reminder_date).toLocaleDateString()} 
                        ({selectedItem.followup_reminder_days} days after contact)
                      </p>
                    ) : (
                      <p className="text-sm text-blue-700 mt-1">No reminder set</p>
                    )}
                  </div>
                )}
                
                {/* Meeting Info (for Meeting Scheduled stage) */}
                {selectedItem.stage === "meeting_scheduled" && (
                  <div className="space-y-3">
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <p className="text-sm font-medium text-purple-800 flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Meeting Details
                      </p>
                      {selectedItem.meeting_date && (
                        <p className="text-sm text-purple-900 mt-1">
                          {new Date(selectedItem.meeting_date).toLocaleString()}
                        </p>
                      )}
                      {selectedItem.meeting_link && (
                        <a 
                          href={selectedItem.meeting_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-purple-600 hover:underline flex items-center gap-1 mt-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Join Meeting
                        </a>
                      )}
                    </div>
                    <Button 
                      className="w-full" 
                      onClick={() => navigate("/strategy")}
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Prepare for Meeting
                    </Button>
                  </div>
                )}
                
                {/* Term Sheet Resources */}
                {selectedItem.stage === "term_sheet" && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-medium text-green-800 flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      Term Sheet Resources
                    </p>
                    <div className="mt-2 space-y-2">
                      <a 
                        href="https://www.ycombinator.com/documents" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-green-600 hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        YC SAFE Templates
                      </a>
                      <a 
                        href="https://nvca.org/model-legal-documents/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-green-600 hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        NVCA Model Documents
                      </a>
                    </div>
                  </div>
                )}
                
                {/* Activity Log */}
                {selectedItem.activities?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-slate-600" />
                      Activity Log
                    </h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedItem.activities.slice().reverse().map((activity, i) => (
                        <div key={i} className="text-sm p-2 bg-slate-50 rounded flex items-start gap-2">
                          <span className="text-slate-400 text-xs whitespace-nowrap">
                            {new Date(activity.created_at).toLocaleDateString()}
                          </span>
                          <span className="text-slate-700">{activity.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <DialogFooter className="flex gap-2 mt-4">
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => handleRemove(selectedItem.pipeline_id)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remove
                </Button>
                <div className="flex-1" />
                {selectedItem.stage !== "term_sheet" && (
                  <Button 
                    variant="outline"
                    onClick={() => {
                      const stages = Object.keys(STAGE_CONFIG);
                      const currentIndex = stages.indexOf(selectedItem.stage);
                      const nextStage = stages[currentIndex + 1];
                      if (nextStage) {
                        openStageModal(selectedItem, nextStage);
                        setShowDetailModal(false);
                      }
                    }}
                  >
                    Move to Next Stage
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
                {selectedItem.primary_contact_email && (
                  <Button onClick={() => { openEmailModal(selectedItem); setShowDetailModal(false); }}>
                    <Mail className="w-4 h-4 mr-1" />
                    Send Email
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-teal-600" />
              Send Email to {selectedItem?.firm_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">To</label>
              <Input
                value={emailData.to_email}
                onChange={(e) => setEmailData({ ...emailData, to_email: e.target.value })}
                placeholder="investor@firm.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Subject</label>
              <Input
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                placeholder="Subject line"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Message</label>
              <Textarea
                value={emailData.body}
                onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                placeholder="Write your message..."
                rows={6}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Follow-up reminder in</label>
              <Select 
                value={String(emailData.auto_followup_days)}
                onValueChange={(v) => setEmailData({ ...emailData, auto_followup_days: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="5">5 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="10">10 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={sending}>
              {sending ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Stage Change Modal */}
      <Dialog open={showStageModal} onOpenChange={setShowStageModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Move to {STAGE_CONFIG[stageData.stage]?.label}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {stageData.stage === "contacted" && (
              <div>
                <label className="text-sm font-medium text-slate-700">Set follow-up reminder</label>
                <Select 
                  value={String(stageData.followup_reminder_days)}
                  onValueChange={(v) => setStageData({ ...stageData, followup_reminder_days: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="5">5 days</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="10">10 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {stageData.stage === "meeting_scheduled" && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700">Meeting Date & Time</label>
                  <Input
                    type="datetime-local"
                    value={stageData.meeting_date}
                    onChange={(e) => setStageData({ ...stageData, meeting_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Meeting Link (optional)</label>
                  <Input
                    value={stageData.meeting_link}
                    onChange={(e) => setStageData({ ...stageData, meeting_link: e.target.value })}
                    placeholder="https://zoom.us/..."
                  />
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStageModal(false)}>Cancel</Button>
            <Button onClick={() => handleStageChange(selectedItem?.pipeline_id, stageData.stage)}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
